import React, { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import moment from 'moment'
import {
  makeStyles,
  ConfirmationDialog,
  Grid,
  useCrmDialogs,
  useUser,
  useUserPermissions,
  DetailsContainer,
  TagContainer,
  EditTagsDialog,
  useClientOptions,
  DocsContainer,
  DetailDocList,
  Tabs,
  Tab,
  useMediaQuery,
  useIsMobile
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { Redirect, useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import DocumentTitle from 'src/components/common/DocumentTitle'
import { getCompanySourceDetail } from 'src/components/common/sourceDetails'
import BasePage from 'src/components/layouts/BasePage'
import { useSnackbar } from 'notistack'
import { useCompanyQuery, useSaveEventMutation } from '@paintscout/api'
import type { Company, Contact, Deal, NoteActivity } from '@paintscout/api'
import { Sentry } from 'src/util/appSentry'
import {
  formatPhoneNumber,
  getFeatures,
  getObjectLabels,
  getTags,
  isUserAssigned,
  isUserOwner,
  uuid
} from '@paintscout/util/builder'
import type { DashboardEstimator } from '@paintscout/util/builder'
import { removeAuth0Prefix } from '@paintscout/util'
import { useCompanyMutations, useContactMutations, useDealMutations, useNoteMutations } from 'shared/hooks'
import {
  CompanyDialog,
  CreateQuoteDialog,
  TaskDialog,
  DealDialog,
  EventDialog,
  ContactDialog
} from 'src/components/dialogs'
import type { QuoteTag, CalendarEvent, QuoteDocument, QuoteFile } from 'paintscout'
import { EditItemFilesDialog, EditIntegrationItemFilesDialog } from '@paintscout/react-quote'
import EventDocList from 'src/components/common/EventTable/EventDocList'
import DealDocList from 'src/components/common/DealTable/DealDocList'
import QuoteDocList from 'src/components/common/QuoteTable/QuoteDocList'
import { jsonToLucene } from 'json-to-lucene'
import TaskDocList from 'src/components/common/TaskTable/TaskDocList'
import NotesDocList from 'src/components/common/NotesDocList'
import FilesDocList from 'src/components/common/QuoteTable/FilesDocList'
import NoteDialog from 'src/components/dialogs/NoteDialog'
import { useFlags } from 'shared/components/common/FlagsProvider'
import ContactSearchDialogNew from 'src/components/dialogs/ContactSearchDialog/ContactSearchDialogNew'
import striptags from 'striptags'
import uniq from 'lodash/uniq'
import { getUrlQuery, setUrlQuery } from 'shared/util/routing'
import { goToDeal } from 'src/util/routing'
import { ChatActivityComponent } from '../../dashboard/Dashboard/ChatActivityComponent'
import DashboardActivity from '../../dashboard/Dashboard/DashboardActivity'
import OwnedHeader from '../OwnedHeader/OwnedHeader'
import EditCompanyShim from './EditCompanyShim'
import CompanyContactContainer from './CompanyContactContainer'

const DETAILS_ACTIVITY_TYPE = 'details.activityType'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    body: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexDirection: 'row',
      marginTop: theme.spacing(2),
      borderRadius: theme.borderRadius.lg,
      width: '100%'
    },
    gridItem: {
      marginBottom: theme.spacing,
      '& > div': {
        marginBottom: theme.spacing(2)
      }
    },
    tabs: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center'
    }
  }),
  { name: 'EditCompany' }
)

function EditCompany() {
  const classes = useStyles()
  const match = useRouteMatch<{ id: string }>()
  const id = decodeURIComponent(match.params.id)
  const { enqueueSnackbar } = useSnackbar()
  const { options } = useClientOptions()
  const { crmContext, dismissAllDialogs, dismissDialog, openDialog, setCrmContext } = useCrmDialogs()

  const objectLabels = getObjectLabels({ options })
  const { user, trialExpired, isCrew } = useUser()
  const {
    permissionToCreateQuote,
    permissionToCreateInvoice,
    permissionToEditAssignedContacts,
    permissionToEditAllContacts
  } = useUserPermissions()
  const { loading, data, error } = useCompanyQuery({ variables: { id }, fetchPolicy: 'cache-and-network' })
  const { saveCompany, deleteCompanies } = useCompanyMutations()
  const [company, setCompany] = useState(data?.company)
  const history = useHistory()
  const [refreshToggle, setRefreshToggle] = useState(false)
  const [activityRefreshToggle, setActivityRefreshToggle] = useState(false)
  const isMobile = useIsMobile()

  const companyTags = getTags({ options, keys: company?.tags ?? [] })
  const companyFiles = getDocFiles(company)
  const { saveContact } = useContactMutations()
  const { deleteNote, updateNote, createNote } = useNoteMutations()
  const [saveEvent] = useSaveEventMutation()
  const { saveDeal } = useDealMutations()

  const { crm: crmFlag } = useFlags()
  const features = getFeatures({ options })
  const hasTags = !!(features.quotes?.tags?.enabled || crmFlag)
  const hasSources = crmFlag

  const isPrint = useMediaQuery('print')
  const isSm = useMediaQuery('sm')
  const isLg = !isSm || isPrint

  const location = useLocation()
  const {
    activityTab = 'all',
    eventTab = isMobile ? 'details' : crmFlag && !isCrew ? 'events' : 'quotes',
    viewTab: _viewTab = 'details'
  } = getUrlQuery<{
    activityTab: string
    eventTab: string
    viewTab: 'details' | 'activities'
  }>({ location })

  const [viewTab, setViewTab] = useState(_viewTab)
  const [activeEventTabValue, setActiveEventTabValue] = useState(eventTab)
  const [activeActivityTabValue, setActiveActivityTabValue] = useState(activityTab)

  function getEventsTabs({
    isMobile,
    crmFlag,
    permissionToCreateQuote,
    permissionToCreateInvoice
  }: {
    isMobile: boolean
    crmFlag: boolean
    permissionToCreateQuote: boolean
    permissionToCreateInvoice: boolean
  }) {
    return [
      isMobile ? { label: 'Details', value: 'details' } : null,
      isMobile ? { label: 'Contacts', value: 'contacts' } : null,
      crmFlag && !isCrew && { label: 'Events', value: 'events' },
      crmFlag && !isCrew && { label: 'Deals', value: 'deals' },
      { label: objectLabels.quote.plural, value: 'quotes', allowNew: permissionToCreateQuote },
      { label: 'Invoices', value: 'invoices', allowNew: permissionToCreateInvoice },
      crmFlag && !isCrew && { label: 'Tasks', value: 'tasks' }
    ].filter(Boolean)
  }

  const eventsTab = getEventsTabs({ isMobile, crmFlag, permissionToCreateQuote, permissionToCreateInvoice }).map(
    (tab) => ({ ...tab, active: tab.value === activeEventTabValue })
  )

  function getActivitiesTabs({ crmFlag }: { crmFlag: boolean }) {
    return [
      { label: 'All Activity', value: 'all' },
      { label: 'Chat History', value: 'chat' },
      crmFlag && !isCrew && { label: 'Notes', value: 'notes' },
      crmFlag && !isCrew && { label: 'Files', value: 'files' }
    ].filter(Boolean)
  }

  const activitiesTab = getActivitiesTabs({ crmFlag }).map((tab) => ({
    ...tab,
    active: tab.value === activeActivityTabValue
  }))

  const quoteListQuery = (type: string) =>
    jsonToLucene({
      filters: [
        {
          type: 'or',
          value: [
            { id: 'contact.companyDocId', type: 'equal', value: company?._id },
            { id: 'secondaryContact.companyDocId', type: 'equal', value: company?._id },
            { id: 'contact.company', type: 'equal', value: company?.name }
          ]
        },
        { id: 'is_invoice', type: 'equal', value: type === 'quotes' ? false : true },
        {
          id: 'trashed',
          type: 'equal',
          not: true,
          value: true
        }
      ]
    })

  const activityQuery = jsonToLucene({
    filters: [
      {
        id: DETAILS_ACTIVITY_TYPE,
        type: 'in',
        not: true,
        value: ['edited', 'owner-changed', 'message', 'event']
      },
      {
        id: 'details.message.deleted',
        type: 'equal',
        not: true,
        value: true
      },
      {
        id: 'docIds',
        type: 'in',
        not: false,
        value: [id]
      }
    ]
  })

  const chatQuery = jsonToLucene({
    filters: [
      {
        id: DETAILS_ACTIVITY_TYPE,
        type: 'equal',
        value: 'message'
      },
      {
        id: 'details.message.deleted',
        type: 'equal',
        not: true,
        value: true
      },
      {
        id: 'details.contact.companyId',
        type: 'equal' as const,
        value: id
      }
    ]
  })

  const notesQuery = jsonToLucene({
    filters: [
      {
        id: DETAILS_ACTIVITY_TYPE,
        type: 'equal',
        value: 'note'
      },
      {
        id: 'docIds',
        type: 'in',
        not: false,
        value: [id]
      }
    ]
  })

  let canEdit = false
  if (company) {
    const isAssigned = isUserAssigned(company, user.user_id)
    const isOwner = isUserOwner(company, user.user_id)
    canEdit =
      (isOwner || (isAssigned && permissionToEditAssignedContacts) || permissionToEditAllContacts) && !trialExpired
  }

  useEffect(() => {
    const dataCompany = data?.company ?? null
    if (dataCompany) {
      if (!company || company._rev !== dataCompany._rev) {
        setCompany(dataCompany)
      }

      const companyName = dataCompany?.name || ''
      const companyId = dataCompany?._id || ''
      setCrmContext({
        subtitle: companyName ? `Company - ${companyName}` : '',
        company: { id: companyId, name: companyName, hide: true },
        deal: { search: { postFilter: (deal: Deal) => deal.contact?.companyId === companyId } },
        contact: {
          search: {
            doWildcardQueryOnMount: true,
            autoSelectSingleResult: true,
            filters: [
              {
                id: 'companyDocId',
                type: 'equal',
                value: companyId
              }
            ]
          }
        }
      })
    }
  }, [data])

  useEffect(() => {
    return () => {
      setCrmContext({})
    }
  }, [])

  function handleDeleteCompany() {
    openDialog(ConfirmationDialog, {
      message: <span>This will permanently delete {company.name}.</span>,
      onConfirm: async () => {
        deleteCompanies([company._id]).catch((error) => {
          Sentry.captureException(error)
          enqueueSnackbar('Unable to delete Company', { variant: 'error' })
        })
        setTimeout(() => {
          history.push('/people/companies', { fullLoad: true })
          dismissDialog()
        }, 1000)
      },
      onCancel: dismissDialog
    })
  }

  function handleEditCompany() {
    openDialog(CompanyDialog, {
      companyId: id,
      onConfirm: (updatedCompany: Company) => {
        setCompany(updatedCompany)
        if (updatedCompany?.tags?.length > companyTags.length) {
          setTimeout(() => {
            setActivityRefreshToggle((prev) => !prev)
          }, 2000)
        }
        if (isMobile) {
          handleSetViewTab('details')
          handleEventTabClick('details')
        }
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  const details = useMemo(() => {
    const sourceDetail = getCompanySourceDetail({ company, options, hasSources })

    return [
      company?.address && {
        title: 'Address',
        value: `${company?.address}, ${company?.city}, ${company?.province} ${company?.postal}`,
        canCopy: true,
        link: () => {
          // Handle address click
          const parts = [
            company?.address ?? '',
            company?.city ?? '',
            company?.province ?? '',
            company?.country ?? '',
            company?.postal ?? ''
          ].filter((part) => part.trim() !== '') // Remove empty strings
          const formattedString = parts.join(', ')
          openMap(formattedString)
          function openMap(address) {
            const encodedAddress = encodeURIComponent(address)
            if (navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
              window.open(`maps://?q=${encodedAddress}`, '_blank')
            } else {
              window.open(`https://www.google.com/maps/search/${encodedAddress}`, '_blank')
            }
          }
        }
      },
      company?.email && {
        title: 'Email',
        value: company?.email,
        canCopy: true,
        link: () => window.open(`mailto:${company?.email}`, '_blank')
      },
      company?.phone_number && {
        title: 'Phone',
        value: formatPhoneNumber({
          options,
          phoneNumber: company?.phone_number,
          extension: company?.phone_ext
        }),
        canCopy: true,
        link: () => {
          const phoneNumber = formatPhoneNumber({
            options,
            phoneNumber: company?.phone_number,
            extension: company?.phone_ext
          })
          const formattedNumber = `tel:${phoneNumber}`
          window.open(formattedNumber, '_blank')
        }
      },
      sourceDetail,
      company?.created && {
        title: 'Date Created',
        value: moment(company.created).format(options.options.dateFormat.momentValue)
      }
    ].filter(Boolean)
  }, [company, hasSources, options])

  function handleEditCompanyTags() {
    openDialog(EditTagsDialog, {
      appliedTags: company.tags,
      onConfirm: async (tags: string[]) => {
        try {
          const savedCompany = await saveCompany({
            company: {
              ...company,
              tags
            }
          })
          setCompany(savedCompany.company)
          setTimeout(() => {
            setActivityRefreshToggle((prev) => !prev)
          }, 2000)
        } catch (error) {
          enqueueSnackbar('Unable to save Company', { variant: 'error' })
          Sentry.captureException(error, { tags: { companyId: company?._id } })
        }

        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  const handleContactChange = () => {
    handleSetViewTab('details')
    if (isMobile) {
      handleEventTabClick('contacts')
    }
    setRefreshToggle((prev) => !prev)
    setActivityRefreshToggle((prev) => !prev)
  }

  function getEventTabContent(activeTab: string) {
    switch (activeTab) {
      case 'details':
        return <DetailDocList details={details} />
      case 'events':
        return (
          <EventDocList
            query={jsonToLucene({
              filters: [
                {
                  id: DETAILS_ACTIVITY_TYPE,
                  type: 'equal',
                  value: 'event'
                },
                {
                  id: 'docIds',
                  type: 'in',
                  value: [id]
                },
                { id: 'trashed', type: 'equal', not: true, value: true }
              ]
            })}
            onEventsChange={() => {
              setTimeout(() => {
                setActivityRefreshToggle((prev) => !prev)
              }, 2000)
            }}
          />
        )
      case 'contacts':
        return (
          <CompanyContactContainer
            hideHeader
            companyName={company?.name || ''}
            companyId={id}
            canEdit={canEdit}
            onContactChange={handleContactChange}
          />
        )
      case 'deals':
        return <DealDocList companyId={id} />
      case 'quotes':
        return <QuoteDocList query={quoteListQuery('quotes')} />
      case 'invoices':
        return <QuoteDocList isInvoice query={quoteListQuery('invoices')} />
      case 'tasks':
        return (
          <TaskDocList
            companyId={id}
            onTasksChange={() =>
              setTimeout(() => {
                setActivityRefreshToggle((prev) => !prev)
              }, 2000)
            }
          />
        )
      default:
        return null
    }
  }

  function handleEventTabClick(tab: string) {
    // don't update for details or contacts as they are only in the same group on mobile
    if (!['details', 'contacts'].includes(tab)) {
      setUrlQuery({
        location,
        params: {
          eventTab: tab
        }
      })
    }
    setActiveEventTabValue(tab)
  }

  function handleActivityTabClick(tab: string) {
    setUrlQuery({
      location,
      params: {
        activityTab: tab
      }
    })
    setActiveActivityTabValue(tab)
  }

  function handleSetViewTab(tab: 'details' | 'activities') {
    setUrlQuery({
      location,
      params: {
        viewTab: tab
      }
    })
    setViewTab(tab)
  }

  function handleNoteFileDetails(fileIdString: string): QuoteFile {
    const fileId = fileIdString.split('_')[1]
    const file = companyFiles.find((file) => file.key === fileId)
    return file
  }

  function handleEditNoteFile(activity: NoteActivity) {
    const noteFiles = activity?.details?.versions[0]?.files.map((file) => {
      return {
        ...file,
        title: file.name,
        src: file.url,
        key: file.id
      }
    }) as QuoteFile[]
    const fileDocId =
      activity?.details?.docType === 'company'
        ? company?._id
        : activity?.details?.docType === 'contact'
          ? activity?.details?.contact?.id
          : activity?.details?.docType === 'deal'
            ? activity?.details?.deal?.id
            : ''
    openDialog(EditItemFilesDialog, {
      fileDocId: fileDocId,
      loadFiles: true,
      fileDocType: activity?.details?.docType,
      titleOnly: true,
      files: noteFiles as QuoteFile[],
      index: 0,
      onConfirm: async (newFiles, deletedFiles, fileDoc) => {
        const docFiles = getDocFiles(fileDoc)
        const filesUpdated =
          newFiles.length !== docFiles.length ||
          newFiles.some((file) => {
            const existingFile = docFiles.find((f) => f.key === file.key)
            return existingFile?.title !== file.title || existingFile?.type !== file.type
          })
        if (filesUpdated) {
          const updatedFiles = newFiles.map((file) => {
            // if file is in deletedFiles, mark it as deleted
            const deletedFile = deletedFiles.find((deletedFile) => deletedFile.key === file.key)
            if (deletedFile) {
              return { ...file, deleted: true }
            } else {
              return file
            }
          })
          await handleSaveNote(fileDoc, activity._id, newFiles, updatedFiles, activity.details.note)
          setTimeout(() => {
            setRefreshToggle((prev) => !prev)
          }, 500)
        }

        dismissDialog()
      },
      onCancel: dismissDialog,
      onDelete: (_key, _files) => {}
    })
  }

  function getActivityTabContent(activeTab: string) {
    switch (activeTab) {
      case 'all':
        return (
          <NotesDocList
            docId={company?._id}
            query={activityQuery}
            onSaveNote={handleCreateNote}
            onDeleteNote={handleDeleteNote}
            onEditNote={handleEditNote}
            onEditNoteFiles={handleEditNoteFile}
            onNoteFileDetails={handleNoteFileDetails}
            noResultsMessage="No activity found."
          />
        )
      case 'chat':
        return (
          <DashboardActivity
            query={chatQuery}
            onUpdate={() => {}}
            noResultsMessage="No messages found."
            showContent={true}
            ActivityComponent={ChatActivityComponent}
          />
        )
      case 'notes':
        return (
          <NotesDocList
            docId={company?._id}
            query={notesQuery}
            onSaveNote={handleCreateNote}
            onDeleteNote={handleDeleteNote}
            onEditNote={handleEditNote}
            onNoteFileDetails={handleNoteFileDetails}
            onEditNoteFiles={handleEditNoteFile}
          />
        )
      case 'files':
        return <FilesDocList docId={company?._id} files={companyFiles} onUpload={handleFilesChange} />
      default:
        return null
    }
  }

  const addEvent = async (event: CalendarEvent, eventTemplate?: string) => {
    await saveEvent({
      variables: {
        event,
        eventTemplate
      },
      refetchQueries: ['searchActivities']
    })
  }

  async function handleFilesChange(files: QuoteFile[], replaceExisting: boolean = true, fileDoc?: Deal | Contact) {
    if (fileDoc) {
      const updatedFiles = {
        ...(replaceExisting
          ? Object.values(fileDoc?.files ?? {}).reduce((acc, file: QuoteFile) => {
              acc[file.key] = {
                ...file,
                deleted: true
              }
              return acc
            }, {})
          : fileDoc?.files),
        ...files.reduce((acc, file) => {
          // if this file key already exists and is delted, use the delted one
          if (fileDoc?.files?.[file.key]?.deleted) {
            acc[file.key] = {
              ...fileDoc?.files[file.key]
            }
          } else {
            acc[file.key] = file
          }

          return acc
        }, {})
      }
      if (fileDoc.type === 'contact') {
        const updatedContact: Contact = {
          ...(fileDoc as Contact),
          files: updatedFiles
        }
        await saveContact({ contact: updatedContact, delayedRefetchQueries: ['searchActivities'] })
      } else {
        const updatedDeal: Deal = {
          ...(fileDoc as Deal),
          files: updatedFiles
        }
        await saveDeal({ deal: updatedDeal, delayedRefetchQueries: ['searchActivities'] })
      }
    } else {
      const updatedCompany: Company = {
        ...company,
        files: {
          ...(replaceExisting
            ? Object.values(company?.files ?? {}).reduce((acc, file: QuoteFile) => {
                acc[file.key] = {
                  ...file,
                  deleted: true
                }
                return acc
              }, {})
            : company?.files),
          ...files.reduce((acc, file) => {
            // if this file key already exists and is delted, use the delted one
            if (company?.files?.[file.key]?.deleted) {
              acc[file.key] = {
                ...company?.files[file.key]
              }
            } else {
              acc[file.key] = file
            }

            return acc
          }, {})
        }
      }

      setCompany(updatedCompany)
      const { company: savedCompany } = await saveCompany({
        company: updatedCompany,
        delayedRefetchQueries: ['searchActivities']
      })
      setCompany(savedCompany)
    }
  }

  async function handleCreateNote(files: QuoteFile[], note: string) {
    // Upload the files to company, then create the note activity
    const hasNote = striptags(note).trim().length > 0
    const notedFiles = files.map((file) => ({
      ...file,
      onNote: hasNote
    }))
    if (notedFiles.length > 0) {
      await handleFilesChange(notedFiles, false)
    }
    if (hasNote) {
      await createNote({
        docId: company?._id,
        docType: 'company',
        note,
        files: files.map((file) => file.key)
      })
    }
  }

  async function handleSaveNote(
    noteDoc: Contact | Company | Deal,
    id: string,
    files: QuoteFile[],
    updatedFiles: QuoteFile[],
    note: string
  ) {
    // Upload the files to company, then create the note activity
    const notedFiles = [...files, ...updatedFiles].map((file) => ({
      ...file,
      onNote: true
    }))
    await handleFilesChange(notedFiles, false, noteDoc.type === 'company' ? null : (noteDoc as Deal))
    await updateNote({
      id,
      note,
      files: uniq(files.map((file) => file.key))
    })
    dismissDialog()
  }

  async function handleDeleteNote(note: NoteActivity) {
    const noteDocType = note.details.docType
    openDialog(ConfirmationDialog, {
      title: 'Are you sure?',
      message: `This note ${note?.details?.files?.length > 0 ? 'and its attached files ' : ''}will be permanently deleted from the ${noteDocType} and cannot be undone.`,
      onConfirm: async () => {
        // Check all files on the note, filter out from existing files adn update, skip that update if no files
        await deleteNote(note._id, noteDocType !== 'company')
        if (note?.details?.files?.length > 0 && noteDocType === 'company') {
          const updatedFiles = companyFiles.filter((file) => !note?.details?.files?.includes(file.key))
          handleFilesChange(updatedFiles, true)
        }
        dismissAllDialogs()
      },
      onCancel: dismissDialog
    })
  }

  function handleEditNote(note: NoteActivity) {
    openDialog(NoteDialog, {
      noteId: note._id,
      docId: company?._id,
      onConfirm: async (noteDoc: Company | Contact | Deal, noteText: string, files: QuoteFile[], isDirty?: boolean) => {
        if (isDirty) {
          const removedFiles = note.details.files.filter((fileKey) => !files.some((file) => file.key === fileKey))
          const updatedFiles = getDocFiles(noteDoc).map((file) => {
            if (removedFiles.includes(file.key)) {
              return { ...file, deleted: true }
            }
            return file
          })
          await handleSaveNote(noteDoc, note._id, files, updatedFiles, noteText)
        }
      },
      onCancel: dismissDialog,
      onDelete: async (note: NoteActivity) => {
        await handleDeleteNote(note)
      }
    })
  }

  function handleDeleteCompanyTag(tag: QuoteTag) {
    openDialog(ConfirmationDialog, {
      message: <span>This will remove the tag from the company. You can add it back later if needed.</span>,
      onConfirm: async () => {
        try {
          const { company: savedCompany } = await saveCompany({
            company: {
              ...company,
              tags: company.tags.filter((t) => t !== tag?.name)
            }
          })
          setCompany(savedCompany)
        } catch (error) {
          enqueueSnackbar('Unable to save Company', { variant: 'error' })
          Sentry.captureException(error, { tags: { companyId: company?._id } })
        }
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  if (error) {
    Sentry.captureException(error)
    enqueueSnackbar('Unable to load Company', { variant: 'error' })
    return <Redirect to="/people/companies" />
  }

  const handleNewItemClick = ({ type }: { type: string }) => {
    switch (type) {
      case 'event':
      case 'events':
        openDialog(EventDialog, {
          isNew: true,
          event: {
            id: uuid(),
            contact: {
              id: company._id,
              firstName: company.name,
              phoneNumber: company.phone_number,
              email: company.email,
              companyId: id
            }
          },
          searchCompanyId: id,
          onConfirm: (event: CalendarEvent, eventTemplate: string) => {
            addEvent(event, eventTemplate)
            handleSetViewTab('details')
            handleEventTabClick('events')
            dismissDialog()
            setTimeout(() => {
              setActivityRefreshToggle((prev) => !prev)
            }, 2000)
          }
        })
        break
      case 'deal':
      case 'deals':
        openDialog(DealDialog, {
          onConfirm: (deal: Deal) => {
            // setActiveDialog(uuid())
            // handleSetViewTab('details')
            // handleEventTabClick('deals')
            // setTimeout(() => {
            //   setRefreshToggle((prev) => !prev)
            // }, 2000)
            goToDeal(deal._id)
            dismissDialog()
          },
          onCancel: dismissDialog
        })
        break
      case 'contact':
        openDialog(ContactSearchDialogNew, {
          filters: [
            {
              id: 'companyDocId',
              type: 'exists',
              not: true
            }
          ],
          blockIntegration: true,
          onCreateNew: (queryString: string) => {
            openDialog(ContactDialog, {
              hideCompany: true,
              defaultCompany: {
                id,
                name: company?.name ?? ''
              },
              queryString,
              onConfirm: async (_contact: Contact) => {
                handleSetViewTab('details')
                if (isMobile) {
                  handleEventTabClick('contacts')
                }
                setTimeout(() => {
                  setActivityRefreshToggle((prev) => !prev)
                }, 2000)
                dismissAllDialogs()
              },
              onCancel: dismissAllDialogs
            })
          },
          onConfirm: async (newContact: Contact, _currentDealId?: number | string, _syncProvider?: string) => {
            const updatedContact = {
              ...newContact,
              company: company?.name,
              companyDocId: id
            }
            await saveContact({
              contact: updatedContact
            })
            enqueueSnackbar('Contact successfully linked', { variant: 'success' })
            handleSetViewTab('details')
            if (isMobile) {
              handleEventTabClick('contacts')
            }
            setTimeout(() => {
              setActivityRefreshToggle((prev) => !prev)
            }, 2000)
            dismissAllDialogs()
          },
          onCancel: dismissDialog
        })
        break
      case 'estimate':
      case 'quotes':
        openDialog(ContactSearchDialogNew, {
          filters: null, // pass null so ContactSearch uses useCrmDialogs context filters
          blockIntegration: true,
          onConfirm: async (contact: Contact) => {
            const contactName = `${contact?.first_name ?? ''} ${contact?.last_name ?? ''}`.trim()
            if (contactName) {
              setCrmContext({
                ...crmContext,
                subtitle: 'Contact - ' + contactName
              })
              await new Promise((resolve) => setTimeout(resolve, 100))
            }

            openDialog(CreateQuoteDialog, {
              invoice: false,
              forContact: contact,
              onConfirm: (quote: QuoteDocument) => {
                history.push(`/quotes/new`, { quote })
                dismissAllDialogs()
              },
              onCancel: () => {
                setCrmContext({ ...crmContext, subtitle: company?.name ? 'Company - ' + company.name : '' })
                dismissAllDialogs()
              }
            })
          },
          onCancel: () => {
            setCrmContext({ ...crmContext, subtitle: company?.name ? 'Company - ' + company.name : '' })
            dismissDialog()
          }
        })
        break
      case 'invoices':
      case 'invoice':
        openDialog(ContactSearchDialogNew, {
          filters: null, // pass null so ContactSearch uses useCrmDialogs context filters
          blockIntegration: true,
          onConfirm: async (contact: Contact) => {
            const contactName = `${contact?.first_name ?? ''} ${contact?.last_name ?? ''}`.trim()
            if (contactName) {
              setCrmContext({
                ...crmContext,
                subtitle: 'Contact - ' + contactName
              })
              await new Promise((resolve) => setTimeout(resolve, 100))
            }

            openDialog(CreateQuoteDialog, {
              invoice: true,
              forContact: contact,
              onConfirm: (quote: QuoteDocument) => {
                history.push(`/invoices/new`, { quote })
                dismissAllDialogs()
              },
              onCancel: () => {
                setCrmContext({ ...crmContext, subtitle: company?.name ? 'Company - ' + company.name : '' })
                dismissAllDialogs()
              }
            })
          },
          onCancel: () => {
            setCrmContext({ ...crmContext, subtitle: company?.name ? 'Company - ' + company.name : '' })
            dismissDialog()
          }
        })
        break
      case 'task':
      case 'tasks':
        openDialog(TaskDialog, {
          searchCompanyId: id,
          onConfirm: () => {
            handleSetViewTab('details')
            handleEventTabClick('tasks')
            setTimeout(() => {
              setRefreshToggle((prev) => !prev)
            }, 2000)
            dismissDialog()
          },
          onCancel: dismissDialog
        })

        break
      case 'note':
        openDialog(NoteDialog, {
          docId: company?._id,
          onConfirm: async (
            _noteDoc: Company | Contact | Deal,
            note: string,
            files: QuoteFile[],
            _isDirty?: boolean
          ) => {
            await handleCreateNote(files, note)
            handleSetViewTab('activities')
            handleActivityTabClick('notes')
            dismissDialog()
          },
          onCancel: dismissDialog
        })
        break
      case 'file':
        openDialog(EditItemFilesDialog, {
          fileDocId: company?._id,
          titleOnly: true,
          files: Object.values(company?.files ?? {}) as QuoteFile[],
          index: Object.values(company?.files ?? {}).length,
          onConfirm: async (newFiles, _deletedFiles) => {
            await handleFilesChange(newFiles)
            handleSetViewTab('activities')
            handleActivityTabClick('files')
            setActivityRefreshToggle((prev) => !prev)
            dismissDialog()
          },
          onCancel: () => {
            dismissDialog()
          },
          onDelete: (_key, _files) => {}
        })
        break
      case 'file-companycam': {
        const currentFiles = Object.values(company?.files ?? {}) as QuoteFile[]
        openDialog(EditIntegrationItemFilesDialog, {
          provider: 'companycam',
          maxWidth: 'lg',
          fullWidth: true,
          files: currentFiles,
          onConfirm: (newFiles: QuoteFile[]) => {
            const now = Date.now()
            const existingKeys = new Set(currentFiles.map((f) => f.key))
            const timestampedNewFiles = newFiles
              .filter((f) => !existingKeys.has(f.key))
              .map((f) => ({ ...f, created: f.created ?? now }))
            const mergedFiles = [...currentFiles, ...timestampedNewFiles]
            openDialog(
              EditItemFilesDialog,
              {
                fileDocId: company?._id,
                files: mergedFiles,
                index: currentFiles.length,
                titleOnly: true,
                onConfirm: async (finalFiles, _deletedFiles) => {
                  await handleFilesChange(finalFiles)
                  handleSetViewTab('activities')
                  handleActivityTabClick('files')
                  setActivityRefreshToggle((prev) => !prev)
                  dismissDialog()
                },
                onCancel: dismissDialog,
                onDelete: (_key, _files) => {}
              },
              { replace: true }
            )
          },
          onCancel: dismissDialog
        })
        break
      }
      default:
        break
    }
  }

  return (
    <BasePage>
      <DocumentTitle title="Company" />
      <motion.div key={loading.toString()} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        {loading || !company ? (
          <EditCompanyShim />
        ) : (
          <>
            <OwnedHeader
              type="company"
              roles={['admin', 'sales', 'viewer']}
              item={company}
              canCreateQuotes={permissionToCreateQuote}
              canCreateInvoices={permissionToCreateInvoice}
              handleNewClick={handleNewItemClick}
              onOwnerChange={async (estimatorOption: DashboardEstimator) => {
                if (removeAuth0Prefix(company.owner?.id) === removeAuth0Prefix(estimatorOption.value)) {
                  return
                } else {
                  const newCompany: Company = {
                    ...company,
                    owner: {
                      __typename: 'Owner',
                      id: removeAuth0Prefix(estimatorOption.value),
                      firstName: estimatorOption.label.split(' ')[0],
                      lastName: estimatorOption.label.split(' ')[1]
                    }
                  }
                  setCompany(newCompany)
                  await saveCompany({ company: newCompany })
                }
              }}
              actions={
                canEdit
                  ? [
                      {
                        key: 'edit',
                        label: 'Edit',
                        value: 'edit'
                      },
                      {
                        key: 'delete',
                        label: 'Delete',
                        value: 'delete'
                      }
                    ]
                  : []
              }
              handleAction={(action: string) => {
                switch (action) {
                  case 'edit':
                    handleEditCompany()
                    break
                  case 'delete':
                    handleDeleteCompany()
                    break
                }
              }}
            />
            <div className={classes.body}>
              {/* Large */}
              {isLg && (
                <Grid container spacing={2}>
                  {/* Left */}
                  <Grid item xs={6} className={classes.gridItem} style={{ padding: 0, paddingRight: '8px' }}>
                    <DetailsContainer details={details} handleEdit={canEdit ? handleEditCompany : null} />
                    <CompanyContactContainer
                      companyName={company?.name || ''}
                      companyId={id}
                      canEdit={canEdit}
                      onContactChange={handleContactChange}
                    />
                    {hasTags && (
                      <TagContainer
                        tags={companyTags}
                        handleEdit={handleEditCompanyTags}
                        handleDelete={handleDeleteCompanyTag}
                      />
                    )}
                    <DocsContainer
                      key={String(refreshToggle)}
                      handleTabClick={handleEventTabClick}
                      handleNewClick={handleNewItemClick}
                      tabs={eventsTab}
                    >
                      {getEventTabContent(activeEventTabValue)}
                    </DocsContainer>
                  </Grid>
                  {/* Right */}
                  <Grid item xs={6} className={classes.gridItem} style={{ padding: 0, paddingLeft: '8px' }}>
                    <DocsContainer
                      key={String(activityRefreshToggle)}
                      handleTabClick={handleActivityTabClick}
                      tabs={activitiesTab}
                    >
                      {getActivityTabContent(activeActivityTabValue)}
                    </DocsContainer>
                  </Grid>
                </Grid>
              )}
              {/* Small */}
              {!isLg && (
                <Grid item xs={12} className={classes.gridItem}>
                  <div className={classes.tabs}>
                    <Tabs
                      value={viewTab}
                      onChange={(_ev, newTab) => {
                        handleSetViewTab(newTab)
                      }}
                    >
                      <Tab value="details" label="Overview" />
                      <Tab disabled label="|" />
                      <Tab value="activities" label="Timeline" />
                    </Tabs>
                  </div>
                  {viewTab === 'details' ? (
                    <>
                      <DocsContainer
                        key={String(refreshToggle)}
                        handleTabClick={handleEventTabClick}
                        handleNewClick={handleNewItemClick}
                        tabs={eventsTab}
                      >
                        {getEventTabContent(activeEventTabValue)}
                      </DocsContainer>
                      {hasTags && (
                        <TagContainer
                          tags={companyTags}
                          handleEdit={handleEditCompanyTags}
                          handleDelete={handleDeleteCompanyTag}
                        />
                      )}
                    </>
                  ) : (
                    <DocsContainer
                      key={String(activityRefreshToggle)}
                      handleTabClick={handleActivityTabClick}
                      tabs={activitiesTab}
                    >
                      {getActivityTabContent(activeActivityTabValue)}
                    </DocsContainer>
                  )}
                </Grid>
              )}
            </div>
          </>
        )}
      </motion.div>
    </BasePage>
  )
}

export default EditCompany

export function getDocFiles(doc: Company | Contact | Deal): QuoteFile[] {
  return Object.values(doc?.files ?? {}).filter((file: QuoteFile) => !file.deleted) as QuoteFile[]
}
